import React from 'react'
import { useStableAccessor } from '../use-stable-accessor'

interface Section {
  type: 'numeric' | 'alphabetic'
  value: string
  referenceValue: string
  prefix?: string
  suffix?: string
}

interface SectionProcessed extends Section {
  startIndex: number
  endIndex: number
  selectionStart: number
  selectionEnd: number
}

export interface SectionSpecifier {
  type: 'numeric' | 'alphabetic'
  prefix?: string
  suffix?: string
  placeholder?: string
  increment(state: Section, action: { globalValue: string }): string
  decrement(state: Section, action: { globalValue: string }): string
  tick(
    state: Section,
    action: { globalValue: string; sectionValues: string[]; action: 'change' | 'increment' | 'decrement' | 'paste' }
  ): string
  update(
    state: Section,
    action: { globalValue: string; value: string }
  ): {
    value: string
    referenceValue: string
    goToNextSection?: boolean
  }
}

interface SectionedInputState {
  selectedSectionIndex: number | null
  sections: Section[]
  externalValue: string | null
}

type SectionedInputAction =
  | {
      type: 'change'
      value: string
    }
  | {
      type: 'paste'
      value: string
    }
  | {
      type: 'increment'
    }
  | {
      type: 'syncExternalValue'
      externalValue: string | null
    }
  | {
      type: 'decrement'
    }
  | {
      type: 'goToNextSection'
    }
  | {
      type: 'goToPreviousSection'
    }
  | {
      type: 'select'
      selectionStart: number
    }
  | {
      type: 'blur'
    }

export function BuildUseSectionedInput({
  sections: inputSections,
  externalValueToSectionValues,
  sectionValuesToExternalValue,
  tryParsePaste
}: {
  sections: SectionSpecifier[]
  externalValueToSectionValues(externalValue: string | null): string[]
  sectionValuesToExternalValue(sectionValues: string[]): string | null
  tryParsePaste(value: string): string[] | null
}) {
  function getNextState(state: SectionedInputState, action: SectionedInputAction): SectionedInputState {
    const sections = processSections(state.sections)
    const selectedSectionIndex = state.selectedSectionIndex
    const globalValue = getValue(state.sections)

    const nextSectionIndex =
      selectedSectionIndex === null ? null : Math.min(selectedSectionIndex + 1, state.sections.length - 1)

    function replaceSection(
      newSectionValue: { value: string; referenceValue: string },
      action: 'change' | 'increment' | 'decrement' | 'paste'
    ) {
      if (selectedSectionIndex !== null) {
        const newSectionsPartial = state.sections.map((s, i) =>
          i === selectedSectionIndex
            ? {
                ...newSectionValue,
                type: inputSections[selectedSectionIndex].type,
                prefix: inputSections[selectedSectionIndex].prefix,
                suffix: inputSections[selectedSectionIndex].suffix
              }
            : s
        )
        const prospectiveGlobalValue = getValue(newSectionsPartial)

        const newSections = newSectionsPartial.map((s, i) => {
          const tickValue = inputSections[i].tick(s, {
            globalValue: prospectiveGlobalValue,
            sectionValues: newSectionsPartial.map((s) => s.value),
            action: action
          })
          if (tickValue === s.value) {
            return s
          } else {
            return {
              value: tickValue,
              referenceValue: '',
              type: inputSections[i].type,
              prefix: inputSections[i].prefix,
              suffix: inputSections[i].suffix
            }
          }
        })
        const externalValue = sectionValuesToExternalValue(newSections.map((s) => s.value))
        return { sections: newSections, externalValue }
      } else {
        return { sections: state.sections, externalValue: state.externalValue }
      }
    }

    if (action.type === 'change') {
      if (selectedSectionIndex === null) {
        return getNextState(state, {
          type: 'paste',
          value: action.value
        })
      } else {
        const newValue = inputSections[selectedSectionIndex].update(sections[selectedSectionIndex], {
          globalValue,
          value: ParseSectionedInputValue(action.value, inputSections)[selectedSectionIndex]
        })

        return {
          ...state,
          ...replaceSection(newValue, 'change'),
          selectedSectionIndex: newValue.goToNextSection ? nextSectionIndex : state.selectedSectionIndex
        }
      }
    } else if (action.type === 'increment') {
      if (selectedSectionIndex === null) {
        return state
      } else {
        const newValue = inputSections[selectedSectionIndex].increment(sections[selectedSectionIndex], { globalValue })
        return { ...state, ...replaceSection({ value: newValue, referenceValue: '' }, 'increment') }
      }
    } else if (action.type === 'decrement') {
      if (selectedSectionIndex === null) {
        return state
      } else {
        const newValue = inputSections[selectedSectionIndex].decrement(sections[selectedSectionIndex], { globalValue })
        return { ...state, ...replaceSection({ value: newValue, referenceValue: '' }, 'decrement') }
      }
    } else if (action.type === 'goToNextSection') {
      return { ...state, selectedSectionIndex: nextSectionIndex }
    } else if (action.type === 'goToPreviousSection') {
      if (selectedSectionIndex === 0 || selectedSectionIndex === null) {
        return state
      } else {
        return { ...state, selectedSectionIndex: selectedSectionIndex - 1 }
      }
    } else if (action.type === 'select') {
      const match = sections.findIndex((s) => action.selectionStart >= s.startIndex && action.selectionStart < s.endIndex)
      return { ...state, selectedSectionIndex: match === -1 ? null : match }
    } else if (action.type === 'blur') {
      return { ...state, selectedSectionIndex: null }
    } else if (action.type === 'syncExternalValue') {
      if (action.externalValue !== state.externalValue) {
        const sectionValues = externalValueToSectionValues(action.externalValue)
        const newSections = sectionValues.map((v, i) => ({
          value: v,
          referenceValue: '',
          type: inputSections[i].type,
          prefix: inputSections[i].prefix,
          suffix: inputSections[i].suffix
        }))

        const selectedSectionIndex =
          state.selectedSectionIndex === null || state.selectedSectionIndex > newSections.length - 1
            ? null
            : state.selectedSectionIndex

        return {
          ...state,
          externalValue: action.externalValue,
          sections: newSections,
          selectedSectionIndex
        }
      } else {
        return state
      }
    } else if (action.type === 'paste') {
      const sectionValues = tryParsePaste(action.value) || action.value.split(/[^A-Za-z0-9]+/g)
      const sectionIndex = selectedSectionIndex ?? 0
      if (sectionValues.length + sectionIndex <= sections.length) {
        const newSectionsProposed = sections.map((s, i) =>
          i < sectionIndex || i > sectionValues.length + sectionIndex
            ? s
            : {
                ...inputSections[i].update(s, {
                  globalValue: globalValue,
                  value: sectionValues[i - sectionIndex]
                }),
                type: inputSections[i].type,
                prefix: inputSections[i].prefix,
                suffix: inputSections[i].suffix
              }
        )
        const prospectiveGlobalValue = getValue(newSectionsProposed)

        const newSections = newSectionsProposed.map((s, i) => {
          const tickValue = inputSections[i].tick(s, {
            globalValue: prospectiveGlobalValue,
            sectionValues: newSectionsProposed.map((s) => s.value),
            action: 'paste'
          })
          if (tickValue === s.value) {
            return s
          } else {
            return {
              value: tickValue,
              referenceValue: '',
              type: inputSections[i].type,
              prefix: inputSections[i].prefix,
              suffix: inputSections[i].suffix
            }
          }
        })
        const externalValue = sectionValuesToExternalValue(newSections.map((s) => s.value))
        const newSelectedSectionIndex = sectionValues.length + sectionIndex
        return {
          ...state,
          sections: newSections,
          externalValue,
          selectedSectionIndex:
            newSelectedSectionIndex === sections.length ? newSelectedSectionIndex - 1 : newSelectedSectionIndex
        }
      } else {
        return getNextState(state, { type: 'change', value: action.value })
      }
    } else {
      throw new Error(`Unrecognized action ${JSON.stringify(action)}`)
    }
  }

  return function useSectionedInput(props: useSectionedInputParams): useSectionedInputResult {
    const getOnChange = useStableAccessor(props.onChange)
    const [state, setState] = useStateWithCallback<SectionedInputState>(() => {
      const values = props.value === undefined ? props.defaultValue || null : props.value || null
      const sectionValues = externalValueToSectionValues(values)
      return {
        selectedSectionIndex: null,
        sections: inputSections.map((s, i) => ({
          ...s,
          value: sectionValues[i] || s.placeholder || '',
          referenceValue: ''
        })),
        externalValue: props.value === undefined ? props.defaultValue || null : props.value || null
      }
    })

    const updateSelection = React.useCallback(
      (input: HTMLInputElement, focus?: boolean) => {
        return (state: SectionedInputState) => {
          const processed = processSections(state.sections)
          const selectedSection = state.selectedSectionIndex !== null ? processed[state.selectedSectionIndex] : undefined
          const selection = selectedSection ? [selectedSection.selectionStart, selectedSection.selectionEnd] : null
          if (selection) {
            input?.setSelectionRange(selection[0], selection[1])
          }

          // sync internal state with external value
          const onChangeProp = getOnChange()
          onChangeProp && onChangeProp(state.externalValue)
        }
      },
      [getOnChange]
    )

    const propsValue = props.value
    // sync external value with internal state
    React.useEffect(() => {
      const value = propsValue
      if (value !== undefined) {
        setState((s) => {
          if (s.externalValue !== value) {
            return getNextState(s, { type: 'syncExternalValue', externalValue: value })
          } else {
            return s
          }
        })
      }
    }, [propsValue, setState])

    const sections = React.useMemo(() => {
      return processSections(state.sections)
    }, [state.sections])

    const selection: [number, number] | null = React.useMemo(() => {
      const selectedSection = state.selectedSectionIndex !== null ? sections[state.selectedSectionIndex] : undefined
      return selectedSection ? [selectedSection.selectionStart, selectedSection.selectionEnd] : null
    }, [state.selectedSectionIndex, sections])

    const value = React.useMemo(() => {
      return getValue(state.sections)
    }, [state.sections])

    const onChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget
        console.log('onChange', input.value)
        /**
         * At times, the browser autofill tries to update the input value with empty string.
         * We don't want to update the state with empty string as these are sectioned inputs.
         */
        if (!input.value) {
          return
        }
        setState((s) => {
          const nextState = getNextState(s, { type: 'change', value: input.value })
          return nextState
        }, updateSelection(input))
      },
      [setState, updateSelection]
    )

    const onSelect = React.useCallback(
      (event: React.SyntheticEvent<HTMLInputElement>) => {
        const input = event.currentTarget
        setState((s) => {
          const nextState = getNextState(s, { type: 'select', selectionStart: input.selectionStart ?? 0 })
          return nextState
        }, updateSelection(input))
      },
      [setState, updateSelection]
    )
    const onMouseUp = React.useCallback(
      (event: React.SyntheticEvent<HTMLInputElement>) => {
        event.preventDefault()
        const input = event.currentTarget
        setState((s) => {
          const nextState = getNextState(s, { type: 'select', selectionStart: input.selectionStart ?? 0 })
          return nextState
        }, updateSelection(input))
      },
      [setState, updateSelection]
    )

    const onKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        const input = event.currentTarget
        if (event.key === 'ArrowLeft') {
          event.preventDefault()
          setState((s) => getNextState(s, { type: 'goToPreviousSection' }), updateSelection(input))
        } else if (event.key === 'ArrowRight') {
          event.preventDefault()
          setState((s) => getNextState(s, { type: 'goToNextSection' }), updateSelection(input))
        } else if (event.key === 'ArrowUp') {
          event.preventDefault()
          setState((s) => getNextState(s, { type: 'increment' }), updateSelection(input))
        } else if (event.key === 'ArrowDown') {
          event.preventDefault()
          setState((s) => getNextState(s, { type: 'decrement' }), updateSelection(input))
        }
      },
      [setState, updateSelection]
    )

    const onPaste = React.useCallback(
      (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()
        const input = event.currentTarget
        setState(
          (s) => getNextState(s, { type: 'paste', value: event.clipboardData.getData('text') }),
          updateSelection(input)
        )
      },
      [setState, updateSelection]
    )

    const onInput = React.useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        const input = event.currentTarget
        console.log('onInput', input.value)
        setState((s) => {
          const nextState = getNextState(s, { type: 'change', value: input.value })
          return nextState
        }, updateSelection(input))
      },
      [setState, updateSelection]
    )

    return {
      inputProps: {
        value: value,
        onSelect,
        onKeyDown,
        onPaste,
        onMouseUp,
        onChange
      },
      selection: selection,
      selectedSection: state.selectedSectionIndex
    }
  }
}

function getValue(sections: Section[]) {
  return sections.map((s) => `${s.prefix || ''}${s.value}${s.suffix || ''}`).join('')
}

function processSections(sections: Section[]): SectionProcessed[] {
  return sections.reduce((acc, cur, index) => {
    const last = acc[acc.length - 1]
    const startIndex = last?.endIndex || 0
    const endIndex =
      startIndex +
      (cur.prefix?.length || 0) +
      cur.value.length +
      (cur.suffix?.length || 0) +
      (index === sections.length - 1 ? 1 : 0)
    return [
      ...acc,
      {
        ...cur,
        startIndex,
        endIndex,
        selectionStart: startIndex + (cur.prefix?.length || 0),
        selectionEnd: startIndex + (cur.prefix?.length || 0) + cur.value.length
      }
    ]
  }, [] as SectionProcessed[])
}

function ParseSectionedInputValue(value: string, sections: { suffix?: string; prefix?: string }[]) {
  const suffixDividers = sections.map((s, i) => `${s.suffix || ''}${sections[i + 1]?.prefix || ''}`)
  const indices: number[] = []
  return suffixDividers.map((s, i) => {
    const previous = i === 0 ? 0 : indices[i - 1]
    const end = s === '' ? value.length : value.slice(previous).indexOf(s) + previous
    indices.push(end + s.length)
    return value.slice(previous, end)
  })
}

function useStateWithCallback<T>(
  initialValue: T | (() => T)
): [T, (state: T | ((prevState: T) => T), callback?: (value: T) => unknown) => void] {
  const callbackRef = React.useRef<Function | null | undefined>(null)

  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    if (callbackRef.current) {
      callbackRef.current(value)

      callbackRef.current = null
    }
  }, [value])

  const setValueWithCallback = React.useCallback((newValue: T | ((prevState: T) => T), callback?: Function) => {
    callbackRef.current = callback
    return setValue(newValue)
  }, [])

  return [value, setValueWithCallback]
}

export interface useSectionedInputParams {
  value?: string | undefined | null
  defaultValue?: string | undefined | null
  onChange?(value: string | null): unknown
}

export interface useSectionedInputResult {
  selectedSection: number | null
  selection: [number, number] | null
  inputProps: React.ComponentPropsWithoutRef<'input'>
}
